import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Trophy, TrendingUp, Target, RefreshCw, Mailbox, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

// Small dashboard card that fetches the student's latest weekly digest and
// shows best score / biggest jump / weekend suggestion.  Auto-builds one if
// none exists yet.
export default function WeeklyDigestCard() {
  const [digest, setDigest] = useState(null);
  const [loading, setLoading] = useState(true);
  const [rebuilding, setRebuilding] = useState(false);

  const _authHeaders = () => {
    const t = localStorage.getItem('token');
    return t ? { Authorization: `Bearer ${t}` } : {};
  };

  const fetchDigest = async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API}/api/digest/weekly`, { credentials: 'include', headers: _authHeaders() });
      if (r.ok) {
        const data = await r.json();
        setDigest(data.digest);
        // Streak-nudge toast — fires once per session per digest.
        const seenKey = `digest_streak_seen_${data.digest?.week_of}`;
        if (data.digest?.streak_beat && !sessionStorage.getItem(seenKey)) {
          const prev = Math.round(data.digest.prev_biggest_jump_delta || 0);
          const curr = Math.round(data.digest.biggest_jump?.delta || 0);
          toast.success(`🔥 New personal best! Your biggest jump this week is +${curr} — beating last week's +${prev}. Keep going!`, { duration: 6000 });
          sessionStorage.setItem(seenKey, '1');
        }
      }
    } catch (e) { /* ignore — card just stays empty */ }
    finally { setLoading(false); }
  };

  const rebuild = async () => {
    setRebuilding(true);
    try {
      const r = await fetch(`${API}/api/digest/weekly/build`, { method: 'POST', credentials: 'include', headers: _authHeaders() });
      if (r.ok) {
        const data = await r.json();
        setDigest(data.digest);
        toast.success(data.email_sent ? 'Digest rebuilt · Email sent' : 'Digest rebuilt');
      }
    } catch { toast.error('Could not rebuild digest'); }
    finally { setRebuilding(false); }
  };

  useEffect(() => { fetchDigest(); }, []);

  if (loading) {
    return (
      <Card className="border-indigo-500/30" data-testid="weekly-digest-card">
        <CardContent className="p-6 flex items-center justify-center min-h-[140px]">
          <Loader2 className="w-5 h-5 animate-spin text-indigo-500" />
        </CardContent>
      </Card>
    );
  }
  if (!digest) return null;

  return (
    <Card className="border-indigo-500/30 bg-gradient-to-br from-indigo-500/5 via-background to-fuchsia-500/5" data-testid="weekly-digest-card">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm flex items-center gap-2">
          <Mailbox className="w-4 h-4 text-indigo-500" />Your Friday Recap
          <Badge variant="outline" className="text-[9px] ml-auto">Week of {digest.week_of}</Badge>
          <Button size="icon" variant="ghost" onClick={rebuild} disabled={rebuilding} className="w-6 h-6" data-testid="digest-rebuild-btn" title="Rebuild digest">
            <RefreshCw className={`w-3 h-3 ${rebuilding ? 'animate-spin' : ''}`} />
          </Button>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {digest.empty ? (
          <p className="text-xs text-muted-foreground" data-testid="digest-empty">{digest.message}</p>
        ) : (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              <div className="rounded-lg border p-3 bg-emerald-500/5 border-emerald-500/30" data-testid="digest-best">
                <div className="flex items-center gap-2 text-[10px] uppercase tracking-wide text-emerald-600 dark:text-emerald-400 font-semibold">
                  <Trophy className="w-3 h-3" /> Best of the week
                </div>
                <p className="text-sm font-bold mt-1 truncate">{digest.best.label}</p>
                <p className="text-lg font-black text-emerald-500">{digest.best.score}%</p>
              </div>
              {digest.biggest_jump ? (
                <div className="rounded-lg border p-3 bg-sky-500/5 border-sky-500/30" data-testid="digest-jump">
                  <div className="flex items-center gap-2 text-[10px] uppercase tracking-wide text-sky-600 dark:text-sky-400 font-semibold">
                    <TrendingUp className="w-3 h-3" /> Biggest jump
                    {digest.streak_beat && (
                      <Badge className="ml-auto bg-orange-500 text-white text-[9px] px-1.5 py-0" data-testid="digest-streak-badge">🔥 New PB</Badge>
                    )}
                  </div>
                  <p className="text-sm font-bold mt-1 truncate">{digest.biggest_jump.label}</p>
                  <p className="text-xs text-muted-foreground">
                    {Math.round(digest.biggest_jump.from)}% → <span className="font-bold text-sky-500">{Math.round(digest.biggest_jump.to)}%</span>
                    <span className="ml-1 text-emerald-500">+{Math.round(digest.biggest_jump.delta)}</span>
                  </p>
                </div>
              ) : (
                <div className="rounded-lg border p-3 bg-muted/40 border-border" data-testid="digest-jump-empty">
                  <div className="text-[10px] uppercase tracking-wide text-muted-foreground font-semibold">Biggest jump</div>
                  <p className="text-xs text-muted-foreground mt-1">Take at least 2 attempts of the same exam this week to see your improvement.</p>
                </div>
              )}
            </div>
            <div className="rounded-lg border border-indigo-500/30 bg-indigo-500/5 p-3" data-testid="digest-suggestion">
              <div className="flex items-center gap-2 text-[10px] uppercase tracking-wide text-indigo-600 dark:text-indigo-400 font-semibold">
                <Target className="w-3 h-3" /> This weekend
              </div>
              <p className="text-sm font-semibold mt-1">{digest.suggestion.title}</p>
              <p className="text-xs text-muted-foreground mt-1">{digest.suggestion.reason}</p>
            </div>
            <p className="text-[10px] text-muted-foreground">{digest.attempts_count} attempt{digest.attempts_count !== 1 ? 's' : ''} this week · generated {new Date(digest.generated_at).toLocaleString()}</p>
          </>
        )}
      </CardContent>
    </Card>
  );
}
